// @ts-nocheck
import { Dialog, DialogContent, DialogTitle, Grid, InputLabel } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { authSelectors } from 'ducks/auth'
import { orgsActions } from 'ducks/orgs'
import ProUXButton from 'elements/proUXButtons/ProUXButton'
import LoadingDots from 'layout/widgets/LoadingDots'
import { Accordion, AccordionDetails, AccordionSummary, styled, Typography } from 'opensolar-ui'
import { default as React, useEffect, useMemo, useState } from 'react'
import { SelectInput, SimpleForm, TextInput, useNotify, useTranslate } from 'react-admin'
import { useForm } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import restClient from 'restClient'

interface PropTypes {
  onClose: () => void
  isOpen: boolean
}

const MissingFieldsBanner = styled('div')({
  border: '1px solid #f5c518',
  borderRadius: '8px',
  backgroundColor: '#fef9e7',
  padding: '12px 16px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '10px',
  margin: '16px 0',
})

const StyledInfoIcon = styled(InfoOutlined)({
  color: '#b8860b',
  fontSize: '20px',
  marginTop: '2px',
})

const DocusignCATemplateDialog: React.FunctionComponent<PropTypes> = (props) => {
  const [saving, setSaving] = useState<boolean>(false)
  const [fetching, setFetching] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined)
  const [initialValues, setInitialValues] = useState<any>({})
  const translate = useTranslate()
  const dispatch = useDispatch()
  const notify = useNotify()
  const getInitialValues = () => {
    if (initialValues) return initialValues
    else return {}
  }

  const orgId = useSelector(authSelectors.getOrgId) as number
  const enabled: boolean = !!useSelector(authSelectors.getCurrentOrg)?.docusign_config
    ?.enable_california_protection_guide

  useEffect(() => {
    if (enabled && props.isOpen) {
      fetchExistingTemplate()
    }
  }, [enabled, props.isOpen])

  const fetchExistingTemplate = () => {
    setFetching(true)
    const restClientInstance = restClient(window.API_ROOT + '/api')
    restClientInstance('CUSTOM_GET', 'custom', {
      url: 'orgs/' + orgId + '/template_contents/',
      data: {
        template_type: 'CA_consumer_protection',
      },
    })
      .then((response: any) => {
        if (response.data.success) {
          setInitialValues(response.data.field_map)
        }
      })
      .catch((error: any) => {
        console.log('error', error)
      })
      .finally(() => setFetching(false))
  }

  const onSubmit = (vals: any) => {
    setSaving(true)
    const restClientInstance = restClient(window.API_ROOT + '/api')
    restClientInstance('CUSTOM_POST', 'custom', {
      url: 'orgs/' + orgId + '/add_template/',
      data: {
        ...vals,
        template_type: 'CA_consumer_protection',
      },
    })
      .then((response: any) => {
        if (enabled) notify('Your changes have been saved', 'success')
        else notify('Your CA Consumer Protection template is enabled', 'success')
        props.onClose()
        dispatch(orgsActions.updateOrgWithCaTemplate(orgId, true))
        setErrorMsg(undefined)
      })
      .catch((error: any) => {
        console.log('error', error)
        if (error?.body?.message) setErrorMsg(error.body.message)
        else setErrorMsg('Unable to save Docusign template')
      })
      .finally(() => setSaving(false))
  }

  const disconnect = () => {
    setSaving(true)
    const restClientInstance = restClient(window.API_ROOT + '/api')
    restClientInstance('CUSTOM_PUT', 'custom', {
      url: 'orgs/' + orgId + '/disconnect_template/',
      data: {
        template_type: 'CA_consumer_protection',
      },
    })
      .then((response: any) => {
        if (response.data.success) {
          notify('Docusign Template has been disabled', 'success')
          props.onClose()
          dispatch(orgsActions.updateOrgWithCaTemplate(orgId, false))
        }
      })
      .catch((error: any) => {
        console.log('error', error)
      })
      .finally(() => setSaving(false))
  }

  return (
    <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="md" fullWidth>
      <DialogTitle>{translate('California Consumer Protection - Template Guide')}</DialogTitle>
      <DialogContent>
        <div>
          <p>
            {translate(
              'Use the below forms to populate regulatory required consumer protection documents that will be shared with your customer upon receipt of this contract.'
            )}
          </p>
          <p>
            {translate(
              'These templates will be appended to the contract in the same envelope - ensuring you will not be charged for multiple envelopes.'
            )}
          </p>
          <p>
            {translate('Read more about how this works in our')}{' '}
            <a href="https://support.opensolar.com" target="_blank" rel="noopener noreferrer">
              {translate('dedicated support article')}
            </a>
            .
          </p>
          {fetching ? (
            <div
              style={{
                height: '150px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <LoadingDots text="One moment while we fetch your current template..." />
            </div>
          ) : (
            <SimpleForm initialValues={getInitialValues()} toolbar={<div></div>}>
              <FormContents {...props} onSubmit={onSubmit} loading={saving} enabled={enabled} errorMsg={errorMsg} />
            </SimpleForm>
          )}
        </div>
        {enabled && (
          <div
            className="small"
            style={{
              textDecoration: 'underline',
              textAlign: 'center',
              width: '100%',
              margin: '20px 0px',
              cursor: 'pointer',
            }}
            onClick={disconnect}
          >
            {translate('Or click here if you want to disable this Docusign template')}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

interface ContentProps extends PropTypes {
  onSubmit: (vals: any) => void
  loading: boolean
  enabled: boolean
  errorMsg: string | undefined
}

const cpgFields = ['version', 'company_name', 'company_email', 'company_phone', 'cslb_number', 'cslb_classification', 'his_registration']
const solarSupportFields = [
  'solar_subcontractors_used',
  'solar_warranty_page',
  'solar_service_maintenance_page',
  'solar_lease_transfer',
  'solar_lease_transfer_page',
  'solar_is_purchase',
  'solar_is_loan',
]

const FormContents: React.FunctionComponent<ContentProps> = (props) => {
  const form = useForm()
  const translate = useTranslate()

  const onSubmit = () => {
    props.onSubmit(form.getState().values)
  }

  const hasMissingFields = useMemo(() => {
    const values = form.getState().values || {}
    const allFields = [...cpgFields, ...solarSupportFields]
    return allFields.some((field) => !values[field])
  }, [form.getState().values])

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography textVariant="body1Bold">{translate('California Consumer Protection Guide')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <InputLabel>{translate('Consumer Protection Policy Version:')}</InputLabel>
                <SelectInput
                  fullWidth={true}
                  source="version"
                  choices={[
                    { id: '-8-2022', name: '2022 Version' },
                    { id: '-2025', name: '2025 Version' },
                  ]}
                />
                <div className="small" style={{ marginBottom: '10px' }}>
                  {translate(
                    'Changing the version will not impact Docusign envelopes that have already been completed. If you would like to update existing envelopes you can use the Manage Docusign Contract button in the info and/or manage page to reset the incomplete contract.'
                  )}
                </div>
              </Grid>
              <Grid item xs={12}>
                <TextInput label="Company Name" source="company_name" fullWidth style={{ width: '100%' }} />
              </Grid>
              <Grid item xs={4}>
                <TextInput label="Company Email" source="company_email" fullWidth style={{ width: '100%' }} />
              </Grid>
              <Grid item xs={4}>
                <TextInput label="Company Phone" source="company_phone" fullWidth style={{ width: '100%' }} />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  label="Company CSLB License #"
                  source="cslb_number"
                  fullWidth
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  label="Company CSLB License Classification"
                  source="cslb_classification"
                  fullWidth
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  label="Default HIS Registration Number"
                  source="his_registration"
                  fullWidth
                  style={{ width: '100%', marginBottom: '3px' }}
                />
                <span className="small">
                  {translate(
                    'If you want each envelope to pre-populate with a certain HIS registration number you can enter it above. It can always be overwritten by whoever is countersigning the document.'
                  )}
                </span>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      <Grid item xs={12}>
        <Accordion defaultExpanded={false}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography textVariant="body1Bold">{translate('Solar System Supporting Information')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <SelectInput
                  fullWidth={true}
                  source="solar_subcontractors_used"
                  label={translate('Will subcontractors be used to perform this work?')}
                  choices={[
                    { id: 'Yes', name: 'Yes' },
                    { id: 'No', name: 'No' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6} />
              <Grid item xs={6}>
                <TextInput
                  label={translate('Warranty coverage info is in the contract on page')}
                  source="solar_warranty_page"
                  fullWidth
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  label={translate('Service & maintenance info is in the contract on page')}
                  source="solar_service_maintenance_page"
                  fullWidth
                  style={{ width: '100%' }}
                />
              </Grid>

              <Grid item xs={12} style={{ marginTop: '16px' }}>
                <Typography textVariant="body1Bold">{translate('If Lease')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  fullWidth={true}
                  source="solar_lease_transfer"
                  label={translate('Can the customer transfer the system to a new homeowner?')}
                  choices={[
                    { id: 'Yes', name: 'Yes' },
                    { id: 'No', name: 'No' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextInput
                  label={translate('See contract page(s) for transfer info')}
                  source="solar_lease_transfer_page"
                  fullWidth
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={12}>
                <div className="small" style={{ marginBottom: '10px' }}>
                  {translate('This may require the customer to pay off the agreement in full.')}
                </div>
              </Grid>

              <Grid item xs={12} style={{ marginTop: '16px' }}>
                <Typography textVariant="body1Bold">{translate('If Financing')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  fullWidth={true}
                  source="solar_is_purchase"
                  label={translate('Is this a purchase? (Customer will own system)')}
                  choices={[
                    { id: 'Yes', name: 'Yes' },
                    { id: 'No', name: 'No' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Grid>
              <Grid item xs={6}>
                <SelectInput
                  fullWidth={true}
                  source="solar_is_loan"
                  label={translate('Is the contractor facilitating a solar loan?')}
                  choices={[
                    { id: 'Yes', name: 'Yes' },
                    { id: 'No', name: 'No' },
                  ]}
                  style={{ width: '100%' }}
                />
              </Grid>

              <Grid item xs={12}>
                <div className="small" style={{ marginBottom: '10px' }}>
                  {translate(
                    "System and payment option data will be automatically populated based on the customer's selected option in the proposal. These values cannot be edited at any stage."
                  )}
                </div>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>

      {hasMissingFields && (
        <Grid item xs={12}>
          <MissingFieldsBanner>
            <StyledInfoIcon />
            <div>
              <Typography textVariant="body1Bold" style={{ marginBottom: '2px' }}>
                {translate('Missing fields')}
              </Typography>
              <Typography textVariant="body2">
                {translate(
                  'Whilst these fields are optional, you will want to populate them in order to ensure they are pre-filled in your envelope at customer signing stage.'
                )}
              </Typography>
            </div>
          </MissingFieldsBanner>
        </Grid>
      )}

      <Grid item xs={12}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginBottom: '20px',
          }}
        >
          <div style={{ margin: '0px 20px' }}>
            <ProUXButton type="secondary" label="Cancel" onClick={props.onClose} />
          </div>
          <div style={{ margin: '0px 20px' }}>
            <ProUXButton
              type="primary"
              label={props.enabled ? 'Save' : 'Save'}
              onClick={onSubmit}
              loading={props.loading}
            />
          </div>
        </div>
      </Grid>
    </Grid>
  )
}

export default DocusignCATemplateDialog
